import { ComponentEntity, stringifyEntityRef } from "@backstage/catalog-model";
import { discoveryApiRef, fetchApiRef, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from "@backstage/plugin-catalog-react";
import { AppEnvironment } from "@internal/open-catalog";
import { Box, Card, CardContent, makeStyles, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, TableSortLabel, TextField, withStyles } from "@material-ui/core";
import React, { ChangeEvent, useMemo, useState } from "react";
import useAsync from "react-use/esm/useAsync";
import { MESSAGING_CHANNEL_NAME_ANNOTATION } from "../../../../../components/catalog/useSchedulerTemplateValues";
import { ALL_TENANTS, ScheduleService } from "../../../../../services/ScheduleService";
import { WorkspacesService } from "../../../../../services/WorkspacesService";
import { ScheduleExecutionsRow } from "./ScheduleExecutionsRow";


const useStyles = makeStyles((theme) => ({
    card: {
        padding: theme.spacing(3),
    },
    filterContainer: {
        display: 'flex',
        justifyContent: 'end',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        '& > *': {
            margin: theme.spacing(1),
            minWidth: 175
        }
    },
    tableContainer: {
        '& *[class^="MuiTableCell-root"]': {
            borderBottom: '1px solid rgb(224, 224, 224)'
        },
        '& *[class^="MuiTableRow-root"]': {
            backgroundColor: 'white',
        },
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1
    },
    sortLabel: {
        color: 'white !important',
        '& *[class^="MuiSvgIcon-root"]': {
            color: 'white !important',
        },
    }
}));

const StyledTableHeadCell = withStyles(() => ({
    head: {
        backgroundColor: '#a0a0a0',
        color: 'white'
    },
}))(TableCell);

export const SchedulingAnalysis = () => {
    const classes = useStyles();
    const fetchApi = useApi(fetchApiRef);
    const discoveryApi = useApi(discoveryApiRef);
    const catalogApi = useApi(catalogApiRef);
    const authApi = useApi(microsoftAuthApiRef);

    const { entity } = useEntity<ComponentEntity>();

    const [environment, setEnvironment] = useState<AppEnvironment>('development');
    const [taskCode, setTaskCode] = useState<string>();
    const [workspaceFilter, setWorkspaceFilter] = useState('');
    const [requesterFilter, setRequesterFilter] = useState('');

    const [orderBy, setOrderBy] = useState('periodStart');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(6);

    const [updateSearch, emitUpdateSearch] = useState(false);

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }

    const handleEnvironmentChange = (event: ChangeEvent<HTMLInputElement>) => {
        setEnvironment(event.target.value as any);
    };

    const workspacesService = useMemo(
        () => new WorkspacesService(fetchApi, authApi, discoveryApi, catalogApi),
        [fetchApi, authApi, discoveryApi, catalogApi],
    );

    const schedules = useAsync(async () => {
        if (workspaceFilter && requesterFilter) {
            const url = await discoveryApi.getBaseUrl('proxy');
            const scheduleService = new ScheduleService(url, fetchApi, environment, authApi);
            const pageRequest = { page, size: rowsPerPage, sort: orderBy, sortDirection: order };
            return await scheduleService.listSchedules(taskCode, workspaceFilter, requesterFilter, pageRequest);
        }
        return undefined;
    }, [discoveryApi, taskCode, workspaceFilter, requesterFilter, environment, orderBy, order, page, rowsPerPage, updateSearch]).value;

    const { value: data } = useAsync(async () => {

        const systemId = WorkspacesService.getSystemId(entity);

        const [responseRequester, systemWorkspaces] = await Promise.all([
            catalogApi.getEntities({
                filter: { kind: 'Component', ['spec.type']: 'Service', ['relations.partof']: [stringifyEntityRef(entity)] }
            }),
            systemId
                ? workspacesService.getAllWorkspacesBySystem(environment, systemId)
                : Promise.resolve([]),
        ]);

        const requesters = responseRequester.items.map(item => item.metadata.annotations?.[MESSAGING_CHANNEL_NAME_ANNOTATION]);
        setRequesterFilter(requesters[0] || '');

        const workspaces = systemWorkspaces.map(item => WorkspacesService.toWorkspaceOption(item));
        workspaces.push({
            id: ALL_TENANTS,
            name: ALL_TENANTS,
            tenant: ALL_TENANTS,
        });

        setWorkspaceFilter(workspaces[0]?.tenant || '');

        return {
            componentsRequesters: requesters,
            componentWorkspaces: workspaces
        };
    }, [catalogApi, workspacesService, entity, environment]);


    const createSortHandler = (property: string) => {
        setOrderBy(property);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    };

    const sortLabel = (property: string, label: string) => (
        <TableSortLabel
            active={orderBy === property}
            direction={orderBy === property ? order : 'asc'}
            onClick={() => createSortHandler(property)}
            className={classes.sortLabel}
        >
            {label}
            {orderBy === property ? (
                <span className={classes.visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
            ) : null}
        </TableSortLabel>
    );

    return (<>
        <Card className={classes.card}>
            <CardContent>
                <Box className={classes.filterContainer}>
                    <TextField
                        label="Task Code"
                        value={taskCode}
                        onChange={(event) => setTaskCode(event.target.value as any)}
                        variant="outlined"
                        size="small"
                    />
                    <TextField select required
                        label="Environment"
                        value={environment}
                        onChange={handleEnvironmentChange}
                        variant="outlined"
                        size="small"
                    >
                        {['development', 'staging', 'production'].map((env) => (
                            <MenuItem key={env} value={env}>
                                {env}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField select required
                        label="Workspace" variant="outlined" margin="normal" size="small"
                        value={workspaceFilter}
                        onChange={(event) => setWorkspaceFilter(event.target.value)}
                    >
                        {data?.componentWorkspaces?.map((workspace) => (
                            <MenuItem key={workspace.tenant} value={workspace.tenant}>
                                {workspace.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField select required
                        label="Requester" variant="outlined" margin="normal" size="small"
                        value={requesterFilter}
                        onChange={(event) => setRequesterFilter(event.target.value)}
                    >
                        {data?.componentsRequesters?.map((requester) => (
                            <MenuItem key={requester} value={requester}>
                                {requester}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                <TableContainer component={Paper} className={classes.tableContainer}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <StyledTableHeadCell />
                                <StyledTableHeadCell>Identifier</StyledTableHeadCell>
                                <StyledTableHeadCell width={200}>
                                    {sortLabel('taskCode', 'Code')}
                                </StyledTableHeadCell>
                                <StyledTableHeadCell>
                                    {sortLabel('state', 'State')}
                                </StyledTableHeadCell>
                                <StyledTableHeadCell>Recurrency</StyledTableHeadCell>
                                <StyledTableHeadCell>
                                    {sortLabel('periodStart', 'Initial Period')}
                                </StyledTableHeadCell>
                                <StyledTableHeadCell>
                                    {sortLabel('periodEnd', 'Final Period')}
                                </StyledTableHeadCell>
                                <StyledTableHeadCell align="center" width={130}>
                                    {sortLabel('executions', 'Executions')}
                                </StyledTableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {schedules &&
                                schedules.content.map((row) => (
                                    <ScheduleExecutionsRow
                                        row={row}
                                        environment={environment}
                                        handleUpdateRows={() => emitUpdateSearch(!updateSearch)} />
                                ))
                            }
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={[6, 10, 15, 20]}
                                    count={schedules?.totalElements || 0}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={(event, pageNumber) => setPage(pageNumber)}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    </>);
}